import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Globe, MessageCircle, Database, Loader2, Send, Clock, Download, Upload, Gauge, FileText, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { notificationsService, systemSettingsService, userSettingsService, getErrorMessage } from '@/services';
import { backupService } from '@/services/backup.service';

interface NotificationForm {
  is_enabled: boolean;
  bot_token: string;
  schedule_enabled: boolean;
  schedule_chat_id: string;
}

export default function Settings() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState<'schedule' | null>(null);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Schedule settings
  const [scheduleStartMinutes, setScheduleStartMinutes] = useState('15');
  const [scheduleMaxRetry, setScheduleMaxRetry] = useState('15');
  const [scheduleGenTime, setScheduleGenTime] = useState('23:55');
  const [epgGenTime, setEpgGenTime] = useState('00:05');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  // Rate limiting settings
  const [playbackDelayMs, setPlaybackDelayMs] = useState('800');
  const [playbackMaxBackoffMs, setPlaybackMaxBackoffMs] = useState('10000');
  const [isSavingRateLimit, setIsSavingRateLimit] = useState(false);

  // Logging settings
  const [verboseLogging, setVerboseLogging] = useState(false);
  const [logRetentionDays, setLogRetentionDays] = useState('7');
  const [isSavingLogging, setIsSavingLogging] = useState(false);

  // Streaming Live settings (per-user)
  const [cdnTokenRefresh, setCdnTokenRefresh] = useState(false);
  const [isSavingStream, setIsSavingStream] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<NotificationForm>({
    defaultValues: {
      is_enabled: false,
      bot_token: '',
      schedule_enabled: false,
      schedule_chat_id: '',
    },
  });

  const isEnabled = watch('is_enabled');

  const fetchSettings = async () => {
    try {
      const [, sysSettings, userSettings] = await Promise.all([
        notificationsService.getSettings().then(settings => {
          reset({
            is_enabled: settings.is_enabled,
            bot_token: settings.bot_token || '',
            schedule_enabled: settings.schedule_enabled,
            schedule_chat_id: settings.schedule_chat_id || '',
          });
        }).catch(() => {
          console.log('No notification settings configured');
        }),
        systemSettingsService.get().catch(() => null),
        userSettingsService.get().catch(() => null),
      ]);
      if (sysSettings) {
        setScheduleStartMinutes(sysSettings.event_start_minutes_before || '15');
        setScheduleMaxRetry(sysSettings.event_max_retry_minutes || '15');
        setScheduleGenTime(sysSettings.schedule_generation_time || '23:55');
        setEpgGenTime(sysSettings.epg_generation_time || '00:05');
        setPlaybackDelayMs(sysSettings.playback_delay_ms || '800');
        setPlaybackMaxBackoffMs(sysSettings.playback_max_backoff_ms || '10000');
        setVerboseLogging(sysSettings.verbose_logging === '1');
        setLogRetentionDays(sysSettings.log_retention_days || '7');
      }
      setCdnTokenRefresh(userSettings?.cdn_token_refresh_enabled === '1');
    } catch (error) {
      console.log('Failed to fetch settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveScheduleSettings = async () => {
    setIsSavingSchedule(true);
    try {
      await systemSettingsService.update({
        event_start_minutes_before: scheduleStartMinutes,
        event_max_retry_minutes: scheduleMaxRetry,
        schedule_generation_time: scheduleGenTime,
        epg_generation_time: epgGenTime,
      });
      toast.success(t('common.saved'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleSaveRateLimitSettings = async () => {
    setIsSavingRateLimit(true);
    try {
      await systemSettingsService.update({
        playback_delay_ms: playbackDelayMs,
        playback_max_backoff_ms: playbackMaxBackoffMs,
      });
      toast.success(t('common.saved'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsSavingRateLimit(false);
    }
  };

  const handleSaveLoggingSettings = async () => {
    setIsSavingLogging(true);
    try {
      await systemSettingsService.update({
        verbose_logging: verboseLogging ? '1' : '0',
        log_retention_days: logRetentionDays,
      });
      toast.success(t('common.saved'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsSavingLogging(false);
    }
  };

  const handleSaveStreamSettings = async () => {
    setIsSavingStream(true);
    try {
      await userSettingsService.update({
        cdn_token_refresh_enabled: cdnTokenRefresh ? '1' : '0',
      });
      toast.success(t('common.saved'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsSavingStream(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const onSubmit = async (data: NotificationForm) => {
    try {
      await notificationsService.updateSettings({
        is_enabled: data.is_enabled,
        bot_token: data.bot_token || undefined,
        schedule_enabled: data.schedule_enabled,
        schedule_chat_id: data.schedule_chat_id || undefined,
      });
      toast.success(t('common.saved'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleTestNotification = async () => {
    setIsTesting('schedule');
    try {
      await notificationsService.test('schedule');
      toast.success(t('settings.testSent'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsTesting(null);
    }
  };

  const handleCreateBackup = async () => {
    setIsBackingUp(true);
    try {
      const blob = await backupService.exportUserData();
      const filename = `backup-${new Date().toISOString().split('T')[0]}.json`;
      backupService.downloadBlob(blob, filename);
      toast.success(t('settings.backupCreated'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreBackup = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      if (!window.confirm(t('settings.restoreConfirm'))) {
        return;
      }

      setIsRestoring(true);
      try {
        const result = await backupService.importUserData(file);
        if (result.success) {
          toast.success(result.message);
          // Reload the page to refresh data
          window.location.reload();
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        toast.error(getErrorMessage(error) || t('common.error'));
      } finally {
        setIsRestoring(false);
      }
    };
    input.click();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('settings.title')}</h1>
        </div>
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('settings.title')}</h1>
        <p className="text-dark-400 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* General Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <Globe className="text-primary-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('settings.general')}</h2>
            <p className="text-dark-400 text-sm">{t('settings.generalSubtitle')}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">{t('settings.language')}</label>
            <select
              value={i18n.language}
              onChange={(e) => {
                i18n.changeLanguage(e.target.value);
                localStorage.setItem('language', e.target.value);
              }}
              className="input w-48"
            >
              <option value="it">{t('common.italian')}</option>
              <option value="en">{t('common.english')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Schedule Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <Clock className="text-primary-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('settings.scheduleTitle')}</h2>
            <p className="text-dark-400 text-sm">{t('settings.scheduleSubtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="label">{t('settings.minutesBefore')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="60"
                value={scheduleStartMinutes}
                onChange={(e) => setScheduleStartMinutes(e.target.value)}
                className="input w-24"
              />
              <span className="text-dark-400 text-sm">{t('common.minutes')}</span>
            </div>
            <p className="text-xs text-dark-500 mt-1">
              {t('settings.minutesBeforeHelp')}
            </p>
          </div>
          <div>
            <label className="label">{t('settings.maxRetry')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="60"
                value={scheduleMaxRetry}
                onChange={(e) => setScheduleMaxRetry(e.target.value)}
                className="input w-24"
              />
              <span className="text-dark-400 text-sm">{t('common.minutes')}</span>
            </div>
            <p className="text-xs text-dark-500 mt-1">
              {t('settings.maxRetryHelp')}
            </p>
          </div>
          <div>
            <label className="label">{t('settings.scheduleGeneration')}</label>
            <input
              type="time"
              value={scheduleGenTime}
              onChange={(e) => setScheduleGenTime(e.target.value)}
              className="input w-32"
            />
            <p className="text-xs text-dark-500 mt-1">
              {t('settings.scheduleGenerationHelp')}
            </p>
          </div>
          <div>
            <label className="label">{t('settings.epgGeneration')}</label>
            <input
              type="time"
              value={epgGenTime}
              onChange={(e) => setEpgGenTime(e.target.value)}
              className="input w-32"
            />
            <p className="text-xs text-dark-500 mt-1">
              {t('settings.epgGenerationHelp')}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSaveScheduleSettings}
            disabled={isSavingSchedule}
            className="btn-primary"
          >
            {isSavingSchedule ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Rate Limiting Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
            <Gauge className="text-amber-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('settings.rateLimitingTitle')}</h2>
            <p className="text-dark-400 text-sm">{t('settings.rateLimitingSubtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">{t('settings.playbackDelay')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="100"
                max="5000"
                step="100"
                value={playbackDelayMs}
                onChange={(e) => setPlaybackDelayMs(e.target.value)}
                className="input w-28"
              />
              <span className="text-dark-400 text-sm">ms</span>
            </div>
            <p className="text-xs text-dark-500 mt-1">
              {t('settings.playbackDelayHelp')}
            </p>
          </div>
          <div>
            <label className="label">{t('settings.maxBackoff')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1000"
                max="60000"
                step="1000"
                value={playbackMaxBackoffMs}
                onChange={(e) => setPlaybackMaxBackoffMs(e.target.value)}
                className="input w-28"
              />
              <span className="text-dark-400 text-sm">ms</span>
            </div>
            <p className="text-xs text-dark-500 mt-1">
              {t('settings.maxBackoffHelp')}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSaveRateLimitSettings}
            disabled={isSavingRateLimit}
            className="btn-primary"
          >
            {isSavingRateLimit ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Streaming Live Settings (per-user) */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <RefreshCw className="text-primary-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('settings.streamingTitle')}</h2>
            <p className="text-dark-400 text-sm">{t('settings.streamingSubtitle')}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="cdn_token_refresh_enabled"
              checked={cdnTokenRefresh}
              onChange={(e) => setCdnTokenRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500 mt-0.5"
            />
            <div>
              <label htmlFor="cdn_token_refresh_enabled" className="text-sm text-dark-200 cursor-pointer">
                {t('settings.cdnTokenRefresh')}
              </label>
              <p className="text-xs text-dark-500 mt-1">
                {t('settings.cdnTokenRefreshHelp')}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSaveStreamSettings}
            disabled={isSavingStream}
            className="btn-primary"
          >
            {isSavingStream ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Logging Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <FileText className="text-indigo-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('settings.loggingTitle')}</h2>
            <p className="text-dark-400 text-sm">{t('settings.loggingSubtitle')}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="verbose_logging"
              checked={verboseLogging}
              onChange={(e) => setVerboseLogging(e.target.checked)}
              className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500 mt-0.5"
            />
            <div>
              <label htmlFor="verbose_logging" className="text-sm text-dark-200 cursor-pointer">
                {t('settings.verboseLogging')}
              </label>
              <p className="text-xs text-dark-500 mt-1">
                {t('settings.verboseLoggingHelp')}
              </p>
            </div>
          </div>

          <div>
            <label className="label">{t('settings.logRetention')}</label>
            <select
              value={logRetentionDays}
              onChange={(e) => setLogRetentionDays(e.target.value)}
              className="input w-48"
            >
              <option value="1">{t('settings.retention1Day')}</option>
              <option value="2">{t('settings.retention2Days')}</option>
              <option value="7">{t('settings.retention7Days')}</option>
              <option value="14">{t('settings.retention14Days')}</option>
              <option value="30">{t('settings.retention30Days')}</option>
            </select>
            <p className="text-xs text-dark-500 mt-1">
              {t('settings.logRetentionHelp')}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSaveLoggingSettings}
            disabled={isSavingLogging}
            className="btn-primary"
          >
            {isSavingLogging ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Telegram Settings */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
              <MessageCircle className="text-primary-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-dark-100">{t('settings.telegram')}</h2>
              <p className="text-dark-400 text-sm">{t('settings.telegramSubtitle')}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_enabled"
                className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                {...register('is_enabled')}
              />
              <label htmlFor="is_enabled" className="text-sm text-dark-200">
                {t('settings.enableNotifications')}
              </label>
            </div>

            {isEnabled && (
              <>
                <div>
                  <label className="label">{t('settings.botToken')}</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="123456789:ABC..."
                    {...register('bot_token')}
                  />
                </div>

                <div className="border-t border-dark-700 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="schedule_enabled"
                        className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                        {...register('schedule_enabled')}
                      />
                      <label htmlFor="schedule_enabled" className="text-sm text-dark-200">
                        {t('settings.scheduleNotifications')}
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={handleTestNotification}
                      disabled={isTesting === 'schedule' || isDirty}
                      title={isDirty ? t('settings.saveBeforeTest') : ''}
                      className="btn-secondary btn-sm"
                    >
                      {isTesting === 'schedule' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Send size={14} />
                      )}
                      Test
                    </button>
                  </div>
                  <div>
                    <label className="label">Schedule {t('settings.chatId')}</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="-100123456789"
                      {...register('schedule_chat_id')}
                    />
                  </div>
                </div>

              </>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </div>
      </form>

      {/* Backup Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-success-50 flex items-center justify-center">
            <Database className="text-success-500" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('settings.backup')}</h2>
            <p className="text-dark-400 text-sm">{t('settings.backupSubtitle')}</p>
          </div>
        </div>

        <p className="text-dark-400 text-sm mb-4">{t('settings.backupDescription')}</p>

        <div className="flex gap-3">
          <button
            onClick={handleCreateBackup}
            disabled={isBackingUp}
            className="btn-secondary"
          >
            {isBackingUp ? (
              <Loader2 size={16} className="animate-spin mr-2" />
            ) : (
              <Download size={16} className="mr-2" />
            )}
            {t('settings.createBackup')}
          </button>
          <button
            onClick={handleRestoreBackup}
            disabled={isRestoring}
            className="btn-secondary"
          >
            {isRestoring ? (
              <Loader2 size={16} className="animate-spin mr-2" />
            ) : (
              <Upload size={16} className="mr-2" />
            )}
            {t('settings.restoreBackup')}
          </button>
        </div>
      </div>
    </div>
  );
}
